/**
 * Claims from the Stytch session JWT payload that this app relies on. The
 * backend projects the POS `user_code` into the `external_id` claim, so it is
 * typed optional: not every session (e.g. tokens minted before the claim was
 * added) is guaranteed to carry it.
 */
export interface StytchSessionJwtClaims {
  sub: string;
  aud: Array<string>;
  iss: string;
  exp?: number;
  iat?: number;
  nbf?: number;
  external_id?: string | null;
  'https://stytch.com/organization'?: {
    organization_id: string;
    slug: string;
  };
}

/**
 * Decodes the base64url payload segment of a JWT into a UTF-8 JSON object.
 * Returns `null` for anything that is not a syntactically valid three-segment
 * JWT (empty token, opaque mock/E2E tokens, truncated values) or whose payload
 * is not a JSON object — callers treat `null` as "no claims available".
 */
export function decodeJwtPayload(token: string | null | undefined): StytchSessionJwtClaims | null {
  if (!token) {
    return null;
  }
  const segments = token.split('.');
  if (segments.length !== 3 || !segments[1]) {
    return null;
  }
  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    return parsed as StytchSessionJwtClaims;
  } catch {
    return null;
  }
}
